import React from 'react';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { Box, Typography } from '@mui/material';
import SpinningBox from 'components/SpinningBox';

const CourtsTable = ({ loaded, courtsData }) => {
  const theme = useTheme();
  const { t } = useTranslation();

  if (!loaded || !courtsData || courtsData.length === 0) {
    return (
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <SpinningBox />
        <Typography
          variant="body2"
          fontWeight={500}
          color={theme.palette.darkGrey[600]}
          ml={1}
        >
          {t('loading')}
        </Typography>
      </Box>
    );
  }

  // Group courts by courtsName (venue name)
  const groupedByVenue = courtsData.reduce((acc, slot) => {
    if (!acc[slot.courtsName]) acc[slot.courtsName] = [];
    acc[slot.courtsName].push(slot);
    return acc;
  }, {});

  const timeToMinutes = (time) => {
    const [h, m] = time.split(':').map(Number);
    return h * 60 + m;
  };

  const sortedTimes = [
    ...new Set(
      courtsData.map((s) => s.timeFrom).filter((time) => time >= '07:00'),
    ),
  ].sort((a, b) => timeToMinutes(a) - timeToMinutes(b));

  const timePairs = [];
  for (let i = 0; i < sortedTimes.length; i += 2) {
    timePairs.push([sortedTimes[i], sortedTimes[i + 1]]);
  }

  return (
    <Box
      sx={{
        overflowX: { xs: 'scroll', sm: 'auto' },
        overflowY: 'auto',
        maxHeight: '100%',
        '::-webkit-scrollbar': {
          width: '6px',
          height: '6px',
        },
        '::-webkit-scrollbar-thumb': {
          backgroundColor: theme.palette.darkGrey[300],
          borderRadius: '4px',
        },
        '::-webkit-scrollbar-track': {
          backgroundColor: theme.palette.darkGrey[200],
        },
      }}
    >
      <Box mb={2} sx={{ display: 'flex', alignItems: 'center' }}>
        <Box display="flex" alignItems="center">
          <Box
            sx={{
              width: 50,
              height: 20,
              backgroundColor: theme.palette.darkGrey[200],
            }}
          />
          <Typography
            variant="body4"
            ml={1}
            color={theme.palette.darkGrey[600]}
            fontWeight={600}
          >
            {t('bookedTime')}
          </Typography>
        </Box>
        <Box display="flex" alignItems="center" ml={2}>
          <Box
            sx={{
              width: 50,
              height: 20,
              backgroundColor: theme.palette.success[400],
            }}
          />
          <Typography
            variant="body4"
            ml={1}
            color={theme.palette.darkGrey[600]}
            fontWeight={600}
          >
            {t('freeTime')}
          </Typography>
        </Box>
      </Box>

      {Object.entries(groupedByVenue).map(([courtsName, slots]) => {
        // Group again by courtId
        const groupedByCourt = slots.reduce((acc, slot) => {
          if (!acc[slot.courtId]) acc[slot.courtId] = [];
          acc[slot.courtId].push(slot);
          return acc;
        }, {});

        return (
          <Box key={courtsName} mb={4}>
            <Typography
              variant="h6"
              fontWeight={700}
              color={theme.palette.darkGrey[700]}
              mb={1}
            >
              {courtsName}
            </Typography>
            <table style={{ width: '100%', minWidth: '600px' }}>
              <thead>
                <tr>
                  <th
                    style={{
                      position: 'sticky',
                      top: 0,
                      left: 0,
                      background: '#fff',
                      zIndex: 2,
                    }}
                  >
                    <Typography variant="body3" fontWeight={600}>
                      {t('court')}
                    </Typography>
                  </th>
                  {timePairs.map(([t1], i) => (
                    <th
                      key={i}
                      style={{ position: 'sticky', top: 0, background: '#fff' }}
                    >
                      <Typography variant="body3" fontWeight={600}>
                        {t1.slice(0, 5)}
                      </Typography>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {Object.entries(groupedByCourt).map(([courtId, courtSlots]) => {
                  const slotMap = {};
                  courtSlots.forEach((slot) => {
                    slotMap[slot.timeFrom] = slot.status;
                  });

                  return (
                    <tr key={courtId}>
                      <td
                        style={{
                          position: 'sticky',
                          left: 0,
                          background: '#fff',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        <Typography
                          variant="body2"
                          fontWeight={500}
                          textAlign="left"
                          sx={{
                            width: { xs: '85px', sm: '100px' },
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {courtId}
                        </Typography>
                      </td>
                      {timePairs.map(([t1, t2], idx) => {
                        const status1 = slotMap[t1] || 'free';
                        const status2 = slotMap[t2] || 'free';

                        return (
                          <td key={idx}>
                            <Box display="flex" justifyContent="center">
                              <Box
                                sx={{
                                  width: '48%',
                                  height: 20,
                                  backgroundColor:
                                    status1 === 'full'
                                      ? theme.palette.darkGrey[200]
                                      : theme.palette.success[400],
                                }}
                              />
                              <Box
                                sx={{
                                  width: '48%',
                                  height: 20,
                                  backgroundColor:
                                    status2 === 'full'
                                      ? theme.palette.darkGrey[200]
                                      : theme.palette.success[400],
                                }}
                              />
                            </Box>
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Box>
        );
      })}
    </Box>
  );
};

export default CourtsTable;
